import { API_BASE_URL } from 'src/utils/api-url';

/**
 * XBIT-4777: the console side of step-up authentication. The service refuses the custody routes with
 * `403 STEP_UP_REQUIRED` until the operator has proved, with an authenticator code, that a person is at the
 * keyboard (`POST /auth/step-up`). The token it answers is kept here, in module variables, per tab: never in
 * any browser storage, so a copy of the stored session does not carry it.
 */

export const STEP_UP_HEADER = 'X-Step-Up-Token';
export const STEP_UP_REQUESTED_EVENT = 'xbit-admin-step-up-requested';

/** A held token with less than this left makes `ensureStepUp` ask again. */
const MIN_REMAINING_MS = 60_000;
/** After a Cancel, a refusal does not reopen the dialog for this long. */
const CANCEL_MEMORY_MS = 30_000;

export interface StepUpGrant {
  stepUpToken: string;
  expiresIn: number;
}

export interface FailedStepUpRequest {
  url?: string;
  /** True when this request is already a step-up re-send. */
  retried?: boolean;
  /** The request carried `_skipStepUp`: it never opens the dialog. */
  skipStepUp?: boolean;
  /** The `X-Step-Up-Token` value the request was sent with, or null when it carried none. */
  sentToken?: string | null;
}

let held: { token: string; expiresAtLocal: number } | null = null;
let reason: string | null = null;
let cancelledAt: number | null = null;
let inFlight: Promise<string | null> | null = null;
let settle: ((token: string | null) => void) | null = null;
let listeners = 0;
const warned = new Set<string>();

/** The token, or null once its local expiry has passed. */
export function getStepUpToken(): string | null {
  if (!held) return null;
  if (Date.now() >= held.expiresAtLocal) {
    held = null;
    return null;
  }
  return held.token;
}

/**
 * Stores a grant. The expiry is measured on this console's own clock from the moment the answer arrives
 * (`expiresIn`); the server's `expiresAt` is never read, so a laptop clock that is off does not matter.
 */
export function setStepUpGrant(grant: StepUpGrant): void {
  held = { token: grant.stepUpToken, expiresAtLocal: Date.now() + grant.expiresIn * 1000 };
  cancelledAt = null;
}

/** Forgets the held token and the cancel memory: logout and the end of the session. */
export function clearStepUp(): void {
  held = null;
  cancelledAt = null;
  reason = null;
}

function clearIfHeld(token: string | null | undefined): void {
  if (held && (!token || held.token === token)) held = null;
}

/** The dialog calls this once the service has accepted a code. */
export function completeStepUp(grant: StepUpGrant): void {
  setStepUpGrant(grant);
  const done = settle;
  settle = null;
  done?.(grant.stepUpToken);
}

/** The dialog calls this on Cancel. */
export function cancelStepUp(): void {
  cancelledAt = Date.now();
  const done = settle;
  settle = null;
  done?.(null);
}

/** The dialog subscribes here. Returns the unsubscribe function. */
export function onStepUpRequested(listener: () => void): () => void {
  if (typeof window === 'undefined') return () => undefined;
  window.addEventListener(STEP_UP_REQUESTED_EVENT, listener);
  listeners += 1;
  return () => {
    window.removeEventListener(STEP_UP_REQUESTED_EVENT, listener);
    listeners -= 1;
  };
}

/**
 * The token to use now. When none with more than 60 s left is held, opens the dialog and resolves to the
 * new token, or null if the operator cancels. Concurrent callers share one dialog. An explicit call (a
 * signing dialog) is a user action, so it opens the dialog even inside the cancel memory.
 */
export function ensureStepUp(): Promise<string | null> {
  if (held && held.expiresAtLocal - Date.now() > MIN_REMAINING_MS) return Promise.resolve(held.token);
  if (inFlight) return inFlight;
  if (typeof window === 'undefined' || listeners === 0) return Promise.resolve(null);
  inFlight = new Promise<string | null>((resolve) => {
    settle = resolve;
  }).finally(() => {
    inFlight = null;
  });
  window.dispatchEvent(new Event(STEP_UP_REQUESTED_EVENT));
  return inFlight;
}

function refusalOf(error: unknown): { status?: number; code?: unknown } {
  const e = error as {
    response?: { status?: number; data?: { code?: unknown } };
    status?: number;
    body?: { code?: unknown };
  } | null;
  // An AxiosError carries the answer in `response`; react-admin's HttpError carries `status` and `body`.
  if (e?.response) return { status: e.response.status, code: e.response.data?.code };
  return { status: e?.status, code: e?.body?.code };
}

/** True for a 403 whose body code is STEP_UP_REQUIRED or STEP_UP_UNAVAILABLE. */
export function isStepUpRefusal(error: unknown): boolean {
  const { status, code } = refusalOf(error);
  return status === 403 && (code === 'STEP_UP_REQUIRED' || code === 'STEP_UP_UNAVAILABLE');
}

/** True only for an absolute URL under the admin API. A relative URL goes to the console's own origin. */
export function isAdminApiUrl(url: unknown): url is string {
  return typeof url === 'string' && url.startsWith(API_BASE_URL);
}

/**
 * What to do with a request the service refused with STEP_UP_REQUIRED. Returns the token to re-send it with,
 * or null to throw the original error. In order:
 *   0. the request already is a step-up re-send: clear the token it carried, never retry again;
 *   1. the request carried the held token and was refused: clear it, then carry on;
 *   2. the request carried no header while a token is held (a guarded route missing from STEP_UP_ROUTES):
 *      re-send with the held token, no dialog, one console.warn per route;
 *   3. `_skipStepUp`: null;
 *   4. a Cancel less than 30 s ago: null, no dialog;
 *   5. otherwise ask for a code.
 */
export async function decideStepUpAfterError(error: unknown, ctx: FailedStepUpRequest = {}): Promise<string | null> {
  const { status, code } = refusalOf(error);
  if (status !== 403 || code !== 'STEP_UP_REQUIRED') return null;
  if (ctx.url && /\/auth\/step-up(\/|\?|$)/.test(ctx.url)) return null;

  if (ctx.retried) {
    clearIfHeld(ctx.sentToken);
    return null;
  }
  if (ctx.sentToken) {
    clearIfHeld(ctx.sentToken);
  } else {
    const current = getStepUpToken();
    if (current) {
      const key = ctx.url ?? '(unknown request)';
      if (!warned.has(key)) {
        warned.add(key);
        console.warn(`[step-up] ${key} was refused without X-Step-Up-Token but is not in STEP_UP_ROUTES; re-sending once with the held token`);
      }
      return current;
    }
  }
  if (ctx.skipStepUp) return null;
  if (cancelledAt !== null && Date.now() - cancelledAt < CANCEL_MEMORY_MS) return null;
  return ensureStepUp();
}

/**
 * XBIT-4870: the reason the next prompt shows ("Approving moves money"). A caller sets it before an action it knows
 * is guarded and clears it with null once the action has settled; the dialog reads it each time it opens.
 */
export function setStepUpReason(next: string | null): void {
  reason = next;
}

export function getStepUpReason(): string | null {
  return reason;
}

/** Test hook: back to a fresh module state. */
export function resetStepUpForTests(): void {
  held = null;
  cancelledAt = null;
  inFlight = null;
  settle = null;
  reason = null;
  warned.clear();
}
